import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

/** Material UI Packages. */
import {
    MenuItem,
    Select,
    Autocomplete,
    TextField,
    Popper,
    Box,
    IconButton,
    Tooltip,
    Link,
    Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGridApiContext } from '@mui/x-data-grid';
import useEnhancedEffect from '@mui/material/utils/useEnhancedEffect';

/** Material UI Icons. */
import SouthIcon from '@mui/icons-material/South';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';

/** Internal Components. */
import { formatDateToMMDDYYYY, formatTypedDateInput, parseTypedDateInput } from 'shared/lib/dateFormatter';

const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': {
        fontSize: '11px',
        width: '100%',
        maxHeight: '200px',
    },
    '& .MuiAutocomplete-option': {
        padding: '5px 5px',
    },
});

/**
 * Column header with a "fill down" icon.
 * Clicking copies the first row's value in this column to every row below it.
 * The label uses the default DataGrid header title styling so it matches other headers.
 */
const FillDownHeader = ({ label, field, onFillDown }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.3,
                width: '100%',
                fontWeight: 500,
            }}
        >
            <span className="MuiDataGrid-columnHeaderTitle">{label}</span>
            <Tooltip title="Fill down: copy the first row value to all rows below">
                <IconButton
                    size="small"
                    tabIndex={-1}
                    onClick={(event) => {
                        event.stopPropagation();
                        onFillDown?.(field);
                    }}
                    sx={{ p: 0.1, color: 'inherit' }}
                >
                    <SouthIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

/** Select Component : Value holds the ID, but the label displays the CONTENT value.*/
const CustomSelectCell = ({ params, options, sx, menuItemStyle, disableValue }) => {
    const apiRef = useGridApiContext();
    const handleChange = (event, newValue) => {
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue.props.value,
        });
        apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
    };

    return (
        <Select
            value={params.value}
            onChange={handleChange}
            autoFocus
            sx={{
                width: '100%',
                border: 'none',
                '& .MuiSelect-select': {
                    fontSize: '11px',
                    padding: '5px',
                },
                ...sx,
            }}
            MenuProps={{
                PaperProps: {
                    style: {
                        maxHeight: 150,
                    },
                },
            }}
            disabled={disableValue}
        >
            {options.map((item) => (
                <MenuItem key={item.id || item} value={item.id || item} sx={{ fontSize: '11px', ...menuItemStyle }}>
                    {item.content || item}
                </MenuItem>
            ))}
        </Select>
    );
};

/** Select Component : Value & label apply the CONTENT value. */
const CustomSelectCell_string = ({ params, options, hasOperationManagerRole, allowedStatuses }) => {
    const apiRef = useGridApiContext();

    const handleChange = (event, newValue) => {
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue.props.value || '',
        });
        apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
    };

    return (
        <Select
            value={params.value ?? ''}
            onChange={handleChange}
            autoFocus
            sx={{
                width: '100%',
                border: 'none',
                height: '100%',
                '& .MuiSelect-select': {
                    fontSize: '11px',
                    p: '3px',
                },
            }}
        >
            {options.map((item) => (
                <MenuItem
                    key={item.id || item}
                    value={item}
                    disabled={
                        allowedStatuses?.length > 0 && !hasOperationManagerRole && !allowedStatuses.includes(item)
                    }
                    sx={{ fontSize: '11px', px: 1 }}
                >
                    {item}
                </MenuItem>
            ))}
        </Select>
    );
};

/** Autocomplete Component : Value holds the ID, but the label displays the CONTENT value. */
const CustomAutocompleteCell = ({ params, options, value, shouldFocus, useCustomComparisons = true }) => {
    const inputRef = useRef(null);

    const handleChange = (event, newValue) => {
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? (typeof newValue === 'string' ? newValue : newValue.id) : null,
        });
    };

    useEffect(() => {
        if (shouldFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [shouldFocus]);

    const getOptionLabel = useMemo(() => {
        return useCustomComparisons ? (option) => option.content : (option) => option;
    }, [useCustomComparisons]);

    const isOptionEqualToValue = useMemo(() => {
        return useCustomComparisons ? (option, value) => option.id === value : undefined;
    }, [useCustomComparisons]);

    return (
        <>
            <Autocomplete
                value={value || null}
                options={options}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                onChange={handleChange}
                disableClearable
                openOnFocus
                renderInput={(inputParams) => (
                    <TextField
                        {...inputParams}
                        inputRef={inputRef}
                        variant="standard"
                        sx={{
                            px: 0.3,
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                        }}
                    />
                )}
                disablePortal={false}
                fullWidth
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    height: '100%',
                    '& .MuiInputBase-input': {
                        fontSize: '11px',
                    },
                    '& .MuiInputBase-root': {
                        p: 0,
                        height: '100%',
                    },
                    '& .MuiFormControl-root': {
                        height: '100%',
                    },
                }}
            />
        </>
    );
};

/**
 * Autocomplete Component : Value & label apply the CONTENT value even though options are object.
 * Options has been conencted with Websocket.
 * */
const CustomAutocompleteCell_string = ({
    params,
    options,
    value,
    handleInputChange,
    sx,
    textFieldStyle,
    shouldFocus,
}) => {
    const inputRef = useRef(null);

    const handleChange = (event, newValue) => {
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? newValue.name : null,
        });
    };

    useEnhancedEffect(() => {
        if (params.hasFocus && inputRef.current) {
            const input = inputRef.current.querySelector('input');
            input.focus();
        }
    }, [params.hasFocus, value]);

    return (
        <>
            <Autocomplete
                ref={inputRef}
                value={value ?? ''}
                options={options}
                getOptionLabel={(option) => option.name || params.value}
                isOptionEqualToValue={(option, value) => option.name === value}
                onChange={handleChange}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={inputRef}
                        variant="standard"
                        sx={{
                            px: 1,
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                            ...textFieldStyle,
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ margin: 0, padding: 0 }}>
                        {option.name}
                    </li>
                )}
                disableClearable
                fullWidth
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    height: '100%',
                    width: '100%',
                    '& .MuiInputBase-input': {
                        fontSize: '11px',
                    },
                    '& .MuiInputBase-root': {
                        p: 0,
                        height: '100%',
                    },
                    '& .MuiFormControl-root': {
                        height: '100%',
                    },
                    ...sx,
                }}
            />
        </>
    );
};

const CustomAutocompleteCell_Vessel = ({ params, options, value, handleInputChange }) => {
    const inputRef = useRef(null);

    const handleChange = (event, newValue) => {
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue == '' ? '' : typeof newValue == 'string' && newValue.length > 0 ? newValue : newValue.name,
        });
    };

    useEffect(() => {
        if (params.isEditable && inputRef.current) {
            inputRef.current.focus();
        }
    }, [params.isEditable]);

    return (
        <>
            <Autocomplete
                freeSolo
                openOnFocus
                options={options}
                value={value ?? ''}
                getOptionLabel={(option) => option.name ?? params.value ?? ''}
                isOptionEqualToValue={(option, value) => {
                    const optionName = option?.name || '';
                    const compareValue = value || '';
                    return optionName === compareValue;
                }}
                onChange={handleChange}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={inputRef}
                        variant="standard"
                        sx={{
                            px: 1,
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li
                        {...props}
                        key={option.id}
                        value={option.name}
                        style={{ margin: 0, padding: 0, paddingBottom: '5px', paddingLeft: '5px' }}
                    >
                        {option.name}
                    </li>
                )}
                disablePortal
                disableClearable
                fullWidth
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    height: '100%',
                    width: '100%',
                    '& .MuiInputBase-input': {
                        fontSize: '11px',
                    },
                    '& .MuiInputBase-root': {
                        p: 0,
                        height: '100%',
                    },
                    '& .MuiFormControl-root': {
                        height: '100%',
                    },
                }}
            />
        </>
    );
};

const CustomAutocompleteCell_TradePartner = ({ params, options, handleInputChange, preventEnterSubmitRow = false }) => {
    const inputRef = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const handleChange = (event, newValue) => {
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue,
        });
    };

    useEffect(() => {
        if (params.isEditable && inputRef.current) {
            inputRef.current.focus();
        }
    }, [params.isEditable]);

    return (
        <>
            <Autocomplete
                value={params.value || ''}
                options={options}
                openOnFocus
                onOpen={() => setDropdownOpen(true)}
                onClose={() => setDropdownOpen(false)}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={handleChange}
                onInputChange={(event, value) => handleInputChange?.(event, value, params)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={inputRef}
                        variant="standard"
                        onKeyDown={
                            preventEnterSubmitRow
                                ? (e) => {
                                      if (e.key === 'Enter' && !dropdownOpen) {
                                          console.log('dropdownOpen2', dropdownOpen);
                                          e.stopPropagation();
                                      }
                                  }
                                : undefined
                        }
                        sx={{
                            px: 1,
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ margin: 0, padding: 0, paddingBottom: '5px' }}>
                        {option.name}
                    </li>
                )}
                disablePortal={false}
                disableClearable
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    height: '100%',
                    width: '100%',
                    '& .MuiInputBase-input': {
                        fontSize: '11px',
                    },
                    '& .MuiInputBase-root': {
                        p: 0,
                        height: '100%',
                    },
                    '& .MuiFormControl-root': {
                        height: '100%',
                    },
                }}
            />
        </>
    );
};

const CustomAutocompleteCell_CodeName = ({ params, options, handleInputChange }) => {
    const inputRef = useRef(null);
    const handleChange = (event, newValue) => {
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue,
        });
    };

    useEffect(() => {
        if (params.isEditable && inputRef.current) {
            inputRef.current.focus();
        }
    }, [params.isEditable]);

    return (
        <>
            <Autocomplete
                value={params.value || ''}
                options={options}
                openOnFocus
                getOptionLabel={(option) => (option?.code && option?.name ? `${option.code} | ${option.name}` : '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={handleChange}
                onInputChange={(event, value) => handleInputChange(event, value, params)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={inputRef}
                        variant="standard"
                        sx={{
                            px: 1,
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ margin: 0, padding: 0, paddingBottom: '5px' }}>
                        {option.code} | {option.name}
                    </li>
                )}
                disablePortal={false}
                disableClearable
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    height: '100%',
                    width: '100%',
                    '& .MuiInputBase-input': {
                        fontSize: '11px',
                    },
                    '& .MuiInputBase-root': {
                        p: 0,
                        height: '100%',
                    },

                    '& .MuiFormControl-root': {
                        height: '100%',
                    },
                }}
            />
        </>
    );
};

/** 링크 컬럼 값을 URL 문자열 배열로 정규화한다(invoice_link, crdr_link 등 공용).
 *  레거시 단일 문자열 값과 신규 JSON 배열 값을 모두 수용하며, 빈 값은 제거한다. */
const normalizeLinks = (value) => {
    const source = Array.isArray(value) ? value : value == null ? [] : [value];
    return source.map((item) => String(item).trim()).filter((item) => item !== '');
};

/** 다중 링크 표시 셀(invoice_link, crdr_link 등 공용). enabled=false 이면 비활성 스타일.
 *  단일이면 "View File", 복수면 "View Files..." + 호버 시 파일 목록 툴팁. 편집은 연필 아이콘. */
const MultiLinkCell = ({ params, enabled = true }) => {
    const links = normalizeLinks(params.value);
    const linkStyle = !enabled ? { color: '#C7C7C7' } : {};
    const handleClick = (event) => {
        if (!enabled) {
            event.preventDefault();
        }
    };
    const startEdit = (event) => {
        event.stopPropagation();
        params.api.startCellEditMode({ id: params.id, field: params.field });
    };

    // 행 높이가 고정이라 링크를 쌓으면 클리핑된다. 셀에는 한 줄 요약만 표시하고
    // (단일: "View File", 복수: "View Files...") 복수일 땐 호버 시 옆 툴팁에 클릭 가능한 파일 목록을 띄운다.
    let content = null;
    if (links.length === 1) {
        content = (
            <Link
                href={links[0]}
                target="_blank"
                rel="noopener"
                style={linkStyle}
                sx={{ fontSize: '11px', whiteSpace: 'nowrap' }}
                onClick={handleClick}
            >
                View File
            </Link>
        );
    } else if (links.length > 1) {
        content = (
            <Tooltip
                placement="right"
                arrow
                title={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
                        {links.map((href, index) => (
                            <Link
                                key={`${href}-${index}`}
                                href={href}
                                target="_blank"
                                rel="noopener"
                                sx={{ color: '#90caf9', fontSize: '11px', whiteSpace: 'nowrap' }}
                                onClick={handleClick}
                            >
                                View File {index + 1}
                            </Link>
                        ))}
                    </Box>
                }
            >
                <Box
                    component="span"
                    sx={{
                        fontSize: '11px',
                        whiteSpace: 'nowrap',
                        color: enabled ? 'primary.main' : '#C7C7C7',
                        textDecoration: 'underline',
                        cursor: 'default',
                    }}
                >
                    View Files...
                </Box>
            </Tooltip>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                width: '100%',
                height: '100%',
            }}
        >
            {content}
            {enabled && (
                <Tooltip title="Edit Link">
                    <IconButton size="small" tabIndex={-1} onClick={startEdit} sx={{ p: 0.1 }}>
                        <EditOutlinedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

/** 다중 링크 편집 셀(invoice_link, crdr_link 등 공용). 셀 오른쪽에 Popper 팝업으로 큰 텍스트박스를 연다.
 *  한 줄에 URL 하나씩 입력하며 배열로 커밋한다(다른 셀 클릭/바깥 클릭 시 저장, Esc/취소 버튼 시 취소). */
const MultiLinkEditCell = ({ params }) => {
    const apiRef = useGridApiContext();
    const { id, field } = params;
    const [text, setText] = useState(() => normalizeLinks(params.value).join('\n'));
    const [anchorEl, setAnchorEl] = useState(null);

    const handleChange = (event) => {
        const nextText = event.target.value;
        setText(nextText);
        const links = nextText
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line !== '');
        apiRef.current.setEditCellValue({ id, field, value: links });
    };

    const handleCancel = (event) => {
        event.stopPropagation();
        // 저장 없이 편집 취소 (변경 무시)
        apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
    };

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* 셀 오른쪽 가장자리에 붙는 보이지 않는 앵커 */}
            <Box ref={setAnchorEl} sx={{ position: 'absolute', top: 0, right: 0, height: '100%' }} />
            {anchorEl && (
                <Popper open anchorEl={anchorEl} placement="right-start" style={{ zIndex: 1300 }}>
                    <Paper elevation={6} sx={{ p: 1, pt: 2.5, ml: 0.5, position: 'relative' }}>
                        <Tooltip title="Cancel">
                            <IconButton
                                size="small"
                                onClick={handleCancel}
                                sx={{ position: 'absolute', top: 2, right: 2, p: 0.25 }}
                            >
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <TextField
                            value={text}
                            onChange={handleChange}
                            onKeyDown={(event) => {
                                // Enter는 줄바꿈으로 쓰고 저장(편집 종료)은 바깥 클릭으로만.
                                if (event.key === 'Enter') {
                                    event.stopPropagation();
                                }
                            }}
                            autoFocus
                            multiline
                            minRows={5}
                            maxRows={14}
                            placeholder="Enter one link on each line"
                            sx={{ width: 380, '& .MuiInputBase-root': { fontSize: '11px' } }}
                        />
                    </Paper>
                </Popper>
            )}
        </Box>
    );
};

/** type: 'date' 컬럼 공용 편집 셀. "MM-DD-YYYY" 전체 입력은 그대로 지원하고,
 *  구분자 없이 4자리(MMDD)만 입력하면 연도를 브라우저의 현재 연도로 채워 완성한다.
 *  존재하지 않는 날짜(예: 0230, 1332)는 커밋 시점에 거부되고 이전 값으로 되돌아간다. */
const DateTypingEditCell = ({ params }) => {
    const apiRef = useGridApiContext();
    const { id, field, value, hasFocus } = params;
    const [inputText, setInputText] = useState(() => formatDateToMMDDYYYY(value));
    const inputRef = useRef(null);

    useEnhancedEffect(() => {
        if (hasFocus && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [hasFocus]);

    // DataGrid가 Enter/Tab 처리 과정에서 같은 이벤트를 계속 전파시키므로, DOM 이벤트의
    // target.value 대신 컴포넌트가 들고 있는 inputText state를 그대로 커밋 대상으로 쓴다.
    const commit = () => {
        const digitCount = inputText.replace(/\D/g, '').length;
        if (digitCount === 0) {
            apiRef.current.setEditCellValue({ id, field, value: null });
            setInputText('');
            return;
        }
        const parsed = parseTypedDateInput(inputText);
        if (parsed) {
            apiRef.current.setEditCellValue({ id, field, value: parsed });
            setInputText(formatDateToMMDDYYYY(parsed));
        } else {
            setInputText(formatDateToMMDDYYYY(value));
        }
    };

    const handleChange = (event) => {
        const formatted = formatTypedDateInput(event.target.value);
        setInputText(formatted);
        // 8자리(MM-DD-YYYY)가 모두 입력되면 타이핑 중에도 즉시 반영한다.
        if (formatted.replace(/-/g, '').length === 8) {
            const parsed = parseTypedDateInput(formatted);
            if (parsed) {
                apiRef.current.setEditCellValue({ id, field, value: parsed });
            }
        }
    };

    return (
        <TextField
            inputRef={inputRef}
            value={inputText}
            onChange={handleChange}
            onBlur={commit}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    commit();
                }
            }}
            placeholder="MM-DD-YYYY"
            variant="standard"
            fullWidth
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& .MuiInputBase-root': {
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    border: 'none',
                },
                '& .MuiInputBase-input': {
                    height: '100%',
                    padding: '0 !important',
                    fontSize: '11px',
                    textAlign: 'center',
                    lineHeight: '1',
                    boxSizing: 'border-box',
                },
            }}
        />
    );
};

export {
    CustomSelectCell,
    CustomSelectCell_string,
    CustomAutocompleteCell,
    CustomAutocompleteCell_string,
    CustomAutocompleteCell_Vessel,
    CustomAutocompleteCell_TradePartner,
    CustomAutocompleteCell_CodeName,
    FillDownHeader,
    MultiLinkCell,
    MultiLinkEditCell,
    DateTypingEditCell,
};
